import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DashboardAPI } from "../../services/api";
import { parseNames } from "../../utils/nameParser";

function normalizeTopRoutes(data, t) {
  const raw =
    (Array.isArray(data) && data) ||
    data?.items ||
    data?.routes ||
    data?.topRoutes ||
    data?.data?.items ||
    data?.data?.routes ||
    data?.data?.topRoutes ||
    [];

  if (!Array.isArray(raw)) return [];

  return raw.map((item, index) => {
    const startName =
      item.startName ||
      item.fromName ||
      item.originName ||
      item.qrName ||
      item.startQrName ||
      item.sub ||
      "";
    const rawDestName =
      item.destinationName ||
      item.toName ||
      item.destName ||
      item.name ||
      item.routeName ||
      t("user.navigation.destination");
    const destinationName = parseNames(rawDestName).primary;
    const route =
      item.route ||
      item.label ||
      (startName ? `${startName} → ${destinationName}` : destinationName);

    return {
      id: item.id || `${route}-${index}`,
      route,
      building: item.building || item.buildingName || item.floorName || "",
      scans: Number(item.scans ?? item.count ?? item.total ?? item.totalScans ?? 0),
    };
  });
}

function normalizeHour(hour) {
  if (typeof hour === "number") return Math.max(0, Math.min(23, hour));
  const matched = String(hour || "").match(/\d{1,2}/);
  return matched ? Math.max(0, Math.min(23, Number(matched[0]))) : 0;
}

function normalizeHourlyData(data) {
  const raw =
    (Array.isArray(data) && data) ||
    data?.items ||
    data?.hours ||
    data?.hourly ||
    data?.data?.items ||
    data?.data?.hours ||
    data?.data?.hourly ||
    [];

  const counts = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    count: 0,
  }));

  if (Array.isArray(raw)) {
    raw.forEach((item) => {
      const hour = normalizeHour(item.hour ?? item.h ?? item.label ?? item.time);
      counts[hour].count = Number(
        item.count ?? item.scans ?? item.total ?? item.value ?? 0
      );
    });
  }

  const max = Math.max(...counts.map((item) => item.count), 0);
  return counts.map((item) => ({
    hour: `${String(item.hour).padStart(2, "0")}:00`,
    hourNumber: item.hour,
    count: item.count,
    value: max > 0 ? Math.max(4, (item.count / max) * 100) : 4,
    isPeak: max > 0 && item.count === max,
  }));
}

function formatHour(hourNumber, lang) {
  if (!Number.isFinite(hourNumber)) return "-";
  const suffix = lang === "th" ? " น." : "";
  return `${String(hourNumber).padStart(2, "0")}:00${suffix}`;
}

function formatHourRange(hourNumber) {
  if (!Number.isFinite(hourNumber)) return "-";
  const start = Math.max(0, Math.min(23, hourNumber));
  const end = (start + 1) % 24;
  return `${String(start).padStart(2, "0")}:00-${String(end).padStart(2, "0")}:00`;
}

function periodLabel(period, t) {
  if (period === "24h") return t("admin.topRoutes.hours24");
  if (period === "7d") return t("admin.topRoutes.days7");
  return t("admin.topRoutes.days30");
}

function peakInRange(chartData, from, to, lang) {
  const items = chartData.filter(
    (item) => item.hourNumber >= from && item.hourNumber <= to
  );
  const peak = items.reduce(
    (best, item) => (item.count > best.count ? item : best),
    { hourNumber: null, count: 0 }
  );
  return peak.count > 0 ? formatHour(peak.hourNumber, lang) : "-";
}

function csvCell(value) {
  return JSON.stringify(value ?? "");
}

function downloadCsv(filename, header, rows) {
  const csv = [
    header.map(csvCell).join(","),
    ...rows.map((row) => header.map((key) => csvCell(row[key])).join(",")),
  ].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function TopRoutesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState("24h"); // "24h", "7d", "30d"
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("most-used"); // "most-used", "peak-stats"
  const [page, setPage] = useState(1);
  const [routesData, setRoutesData] = useState([]);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [routesError, setRoutesError] = useState("");
  const [hourlyData, setHourlyData] = useState(() => normalizeHourlyData([]));
  const [hourlyLoading, setHourlyLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRoutesLoading(true);
    setRoutesError("");
    DashboardAPI.topRoutesAnalytics({ period: timeRange, limit: 100 })
      .then((data) => {
        if (!cancelled) setRoutesData(normalizeTopRoutes(data, t));
      })
      .catch((error) => {
        if (!cancelled) {
          setRoutesData([]);
          setRoutesError(error?.message || t("admin.topRoutes.loadFailed"));
        }
      })
      .finally(() => {
        if (!cancelled) setRoutesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [timeRange, t]);

  useEffect(() => {
    let cancelled = false;
    setHourlyLoading(true);
    DashboardAPI.hourlyDensity(timeRange)
      .then((data) => {
        if (!cancelled) setHourlyData(normalizeHourlyData(data));
      })
      .catch(() => {
        if (!cancelled) setHourlyData(normalizeHourlyData([]));
      })
      .finally(() => {
        if (!cancelled) setHourlyLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [timeRange]);

  const chartData = hourlyData;

  const summaryData = useMemo(() => {
    const peak = chartData.reduce(
      (best, item) => (item.count > best.count ? item : best),
      { hourNumber: null, count: 0 }
    );
    const peakHour = peak.count > 0 ? formatHour(peak.hourNumber, i18n.language) : "-";
    return {
      peakHour,
      description:
        peak.count > 0
          ? t("admin.topRoutes.peakHourPeriodDesc", { hour: peakHour, count: peak.count, period: periodLabel(timeRange, t) })
          : t("admin.topRoutes.noRouteDataPeriodDesc", { period: periodLabel(timeRange, t) }),
      morning: peakInRange(chartData, 5, 11, i18n.language),
      afternoon: peakInRange(chartData, 12, 17, i18n.language),
      evening: peakInRange(chartData, 18, 23, i18n.language),
    };
  }, [chartData, timeRange, t, i18n.language]);

  const rawTableData = routesData;

  const filteredTableData = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return rawTableData;
    return rawTableData.filter(
      (item) =>
        item.route.toLowerCase().includes(q) ||
        item.building.toLowerCase().includes(q)
    );
  }, [searchQuery, rawTableData]);

  const peakTableData = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    const data = chartData.map((item) => ({
      id: item.hour,
      time: formatHourRange(item.hourNumber),
      users: item.count,
      status: item.isPeak ? "PEAK TIME" : "NORMAL",
      isPeak: item.isPeak,
    }));

    if (!q) return data;

    return data.filter(
      (item) =>
        item.time.toLowerCase().includes(q) ||
        item.status.toLowerCase().includes(q)
    );
  }, [chartData, searchQuery]);

  const activeTableData =
    activeTab === "most-used" ? filteredTableData : peakTableData;

  const onExport = () => {
    const safePeriod = String(timeRange || "period").replace(/[^\w-]+/g, "-");
    if (activeTab === "most-used") {
      downloadCsv(
        `top-routes-${safePeriod}.csv`,
        ["route", "building", "scans"],
        filteredTableData
      );
      return;
    }

    downloadCsv(
      `peak-times-${safePeriod}.csv`,
      ["time", "users", "status"],
      peakTableData
    );
  };

  // Pagination simulation
  const itemsPerPage = 6;
  const totalPages = Math.max(Math.ceil(activeTableData.length / itemsPerPage), 1);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return activeTableData.slice(start, start + itemsPerPage);
  }, [activeTableData, page]);

  return (
    <div className="flex flex-col gap-6 font-sans select-none text-slate-800 pb-8">
      {/* HEADER SECTION */}
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="mb-4">
          <div className="flex items-center gap-1 text-slate-900">
            <button
              type="button"
              onClick={() => navigate("/admin")}
              className="p-1 text-3xl font-base rounded-lg hover:bg-slate-100"
              aria-label={t("common.back")}
            >
              ‹
            </button>
            <h1 className="text-xl font-semibold">
              {t("admin.topRoutes.topRoutesScanTitle")}
            </h1>
          </div>
          <p className="p-1 text-sm text-slate-700">
            {t("admin.topRoutes.topRoutesScanSub")}
          </p>
        </div>

        {/* Time period selector pills */}
        <div className="flex items-center bg-white p-0.5 rounded-3xl border border-slate-50 shadow-sm self-start sm:self-center">
          <button
            onClick={() => { setTimeRange("24h"); setPage(1); }}
            className={`px-4 py-1.5 text-xs sm:text-sm font-semibold rounded-2xl transition-all duration-200 ${timeRange === "24h"
              ? "bg-[#005EA4] text-white shadow-md shadow-[#005EA4]/20"
              : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
              }`}
          >
            {t("admin.topRoutes.hours24")}
          </button>
          <button
            onClick={() => { setTimeRange("7d"); setPage(1); }}
            className={`px-4 py-1.5 text-xs sm:text-sm font-semibold rounded-2xl transition-all duration-200 ${timeRange === "7d"
              ? "bg-[#005EA4] text-white shadow-md shadow-[#005EA4]/20"
              : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
              }`}
          >
            {t("admin.topRoutes.days7")}
          </button>
          <button
            onClick={() => { setTimeRange("30d"); setPage(1); }}
            className={`px-4 py-1.5 text-xs sm:text-sm font-semibold rounded-2xl transition-all duration-200 ${timeRange === "30d"
              ? "bg-[#005EA4] text-white shadow-md shadow-[#005EA4]/20"
              : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
              }`}
          >
            {t("admin.topRoutes.days30")}
          </button>
        </div>
      </header>

      {/* TOP GRID (CHART & SUMMARY) */}
      <section className="grid grid-cols-1 xl:grid-cols-[1.6fr_0.9fr] gap-6">
        {/* CHART CONTAINER */}
        <article className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm flex flex-col justify-between min-h-[360px]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-100 pb-4 mb-4">
            <div>
              <h2 className="font-semibold text-slate-900 text-lg">{t("admin.topRoutes.usageOverview")}</h2>
              <p className="text-xs text-slate-600 mt-0.5">{t("admin.topRoutes.hourlyDensitySub")}</p>
            </div>

            {/* Chart Legend */}
            <div className="flex items-center gap-4 text-xs font-semibold text-slate-500">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#005EA4]/20" />
                <span>{t("admin.topRoutes.normal")}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#005EA4]" />
                <span>{t("admin.topRoutes.peakHour")}</span>
              </div>
            </div>
          </div>

          {/* Gorgeous CSS Bar Chart with Micro-animations */}
          <div className="relative flex-1 flex flex-col justify-end pt-4">
            <div className="flex items-end justify-between gap-1 sm:gap-2 h-[200px] px-2 relative group">
              {chartData.map((bar, index) => (
                <div
                  key={index}
                  className="flex-1 flex items-end justify-center group/bar relative h-full"
                >
                  {/* Tooltip on Hover */}
                  <div className="absolute -top-12 scale-0 group-hover/bar:scale-100 transition-all duration-150 origin-bottom bg-slate-900 text-white text-[10px] sm:text-xs py-1.5 px-2.5 rounded-lg shadow-xl font-bold whitespace-nowrap z-30">
                    {bar.hour} • {bar.count.toLocaleString()} {t("admin.qrDetail.times", { count: "" }).trim()}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-900 rotate-45" />
                  </div>

                  {/* Vertical bar */}
                  <div
                    className={`w-full rounded-t-full transition-all duration-500 hover:brightness-95 cursor-pointer relative ${bar.isPeak
                      ? "bg-[#005EA4] hover:bg-[#005EA4]/90 shadow-md shadow-[#005EA4]/20"
                      : "bg-[#005EA4]/20 hover:bg-[#005EA4]/30"
                      }`}
                    style={{ height: `${bar.value}%` }}
                  />
                </div>
              ))}
            </div>

            {hourlyLoading && (
              <div className="absolute inset-x-0 top-1/2 text-center text-xs font-bold text-slate-400">
                {t("admin.topRoutes.loadingHourly")}
              </div>
            )}

            {/* X-Axis labels */}
            <div className="flex justify-between mt-2 pt-2 px-2 text-[10px] font-bold text-slate-500">
              <span>00:00</span>
              <span>04:00</span>
              <span>08:00</span>
              <span>12:00</span>
              <span>16:00</span>
              <span>20:00</span>
              <span>23:59</span>
            </div>
          </div>
        </article>

        {/* GRADIENT SUMMARY CARD */}
        <aside className="bg-[#005EA4] rounded-3xl p-6 shadow-xl text-white flex flex-col justify-between min-h-[360px] relative overflow-hidden">
          {/* Subtle design element */}
          <div className="absolute -right-16 -bottom-16 w-48 h-48 bg-white/5 rounded-full blur-3xl pointer-events-none" />

          <div>
            {/* Header info with glassy trend icon */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-md grid place-items-center shadow-inner">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2.5"
                  stroke="currentColor"
                  className="w-5 h-5 text-white"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-semibold tracking-wide">{t("admin.topRoutes.peakHourSummary")}</h2>

            <p className="text-sm mt-2 font-base leading-relaxed text-blue-100/90 pr-4">
              {summaryData.description}
            </p>
          </div>

          {/* Time breakdowns */}
          <div className="flex flex-col gap-4 mt-6">
            <div className="flex justify-between items-center py-2.5">
              <span className="text-xs sm:text-sm font-base text-white">{t("admin.topRoutes.morningAM")}</span>
              <span className="text-lg sm:text-xl font-base text-white">{summaryData.morning}</span>
            </div>
            <div className="h-[1px] bg-white/10" />

            <div className="flex justify-between items-center py-2.5">
              <span className="text-xs sm:text-sm font-base text-white">{t("admin.topRoutes.afternoonPM")}</span>
              <span className="text-lg sm:text-xl font-base text-white">{summaryData.afternoon}</span>
            </div>
            <div className="h-[1px] bg-white/10" />

            <div className="flex justify-between items-center py-2.5">
              <span className="text-xs sm:text-sm font-base text-white">{t("admin.topRoutes.eveningPM")}</span>
              <span className="text-lg sm:text-xl font-base text-white">{summaryData.evening}</span>
            </div>
          </div>
        </aside>
      </section>

      {/* BOTTOM TABLE AREA */}
      <section className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm flex flex-col gap-5">
        {/* Controls row */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Search Box */}
          <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-4 py-2.5 transition-all w-full lg:max-w-xs ">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
              className="w-5 h-5 text-slate-400"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              className="w-full outline-none font-base text-sm bg-transparent placeholder:text-slate-400 text-slate-800"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              placeholder={t("common.search")}
            />
          </div>

          {/* Inner Navigation Tabs */}
          <div className="flex items-center p-1 rounded-2xl self-start lg:self-center">
            <button
              onClick={() => { setActiveTab("most-used"); setPage(1); }}
              className={`px-4 py-2 text-xs sm:text-sm font-base rounded-2xl transition-all duration-200 ${activeTab === "most-used"
                ? "bg-[#005EA4] text-white shadow-md shadow-[#005EA4]/10"
                : "text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                }`}
            >
              {t("admin.topRoutes.mostUsedPlacesStats")}
            </button>
            <button
              onClick={() => { setActiveTab("peak-stats"); setPage(1); }}
              className={`px-4 py-2 text-xs sm:text-sm font-base rounded-2xl transition-all duration-200 ${activeTab === "peak-stats"
                ? "bg-[#005EA4] text-white shadow-md shadow-[#005EA4]/10"
                : "text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                }`}
            >
              {t("admin.topRoutes.peakTimesStats")}
            </button>
          </div>

          {/* Export Button */}
          <button
            type="button"
            onClick={onExport}
            disabled={routesLoading || hourlyLoading}
            className="flex items-center justify-center gap-2 bg-[#005EA4] hover:bg-[#005EA4]/90 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-base rounded-3xl px-5 py-2.5 text-xs sm:text-sm active:scale-95 transition-transform self-start lg:self-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2.5"
              stroke="currentColor"
              className="w-3.5 h-3.5 text-white"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            {t("admin.topRoutes.exportData")}
          </button>
        </div>

        {/* Table Container */}
        <div className="overflow-x-auto rounded-2xl border border-slate-100 shadow-sm">
          {activeTab === "most-used" ? (
            <table className="w-full text-left border-collapse min-w-[640px]">
              <thead>
                <tr className="bg-slate-50 text-xs font-base text-slate-500 uppercase tracking-wider border-b border-slate-100">
                  <th className="py-4 px-5">
                    {t("admin.topRoutes.startDestCol")}
                  </th>
                  <th className="py-4 px-5 text-left">
                    {t("admin.topRoutes.distanceCol")}
                  </th>
                  <th className="py-4 px-5 text-left">
                    {t("admin.topRoutes.usersCountCol")}
                  </th>
                </tr>
              </thead>

              <tbody>
                {routesLoading ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="py-12 text-center font-base text-slate-500"
                    >
                      {t("admin.topRoutes.loadingRoutes")}
                    </td>
                  </tr>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((row, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-slate-50/50 transition-colors"
                    >
                      <td className="py-4 px-8">
                        <div className="font-semibold text-slate-900 text-base">
                          {row.route}
                        </div>
                        <div className="text-xs text-slate-500 font-base">
                          {row.building}
                        </div>
                      </td>

                      <td className="py-4 px-8 text-left font-semibold text-blue-500 text-sm">
                        {row.distance}
                      </td>

                      <td className="py-4 px-8 text-left font-semibold text-blue-500 text-sm">
                        {(row.scans ?? 0).toLocaleString()}{" "}
                        {t("admin.qrDetail.times", { count: "" }).trim()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={3}
                      className="py-12 text-center font-bold text-slate-400"
                    >
                      {routesError || t("admin.qrDetail.noScanData")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-left border-collapse min-w-[640px]">
              <thead>
                <tr className="bg-slate-50 text-xs font-base text-slate-500 uppercase tracking-wider border-b border-slate-100">
                  <th className="py-4 px-5">
                    {t("admin.topRoutes.timeCol")}
                  </th>
                  <th className="py-4 px-5 text-center">
                    {t("admin.topRoutes.usersPersonCol")}
                  </th>
                  <th className="py-4 px-5 text-center">
                    {t("admin.topRoutes.statusCol")}
                  </th>
                </tr>
              </thead>

              <tbody>
                {hourlyLoading ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="py-12 text-center font-base text-slate-500"
                    >
                      {t("admin.topRoutes.loadingHourly")}
                    </td>
                  </tr>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((row, idx) => (
                    <tr
                      key={row.id || idx}
                      className="hover:bg-slate-50/50 transition-colors"
                    >
                      <td className="py-4 px-8">
                        <div className="font-semibold text-slate-900 text-base">
                          {row.time}
                        </div>
                      </td>

                      <td className="py-4 px-10 text-center font-base text-black text-sm">
                        {(row.users ?? 0).toLocaleString()}
                      </td>

                      <td className="py-4 px-8 text-center">
                        <span className="inline-flex items-center justify-center rounded-full bg-[#005EA4]/10 px-3 py-1 text-xs font-semibold text-[#005EA4]">
                          {row.isPeak ? "PEAK TIME" : "NORMAL"}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={3}
                      className="py-12 text-center font-bold text-slate-400"
                    >
                      {t("admin.qrDetail.noScanData")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              className="w-9 h-9 rounded-xl border border-slate-200 hover:bg-slate-50 font-black text-slate-700 disabled:opacity-50 disabled:hover:bg-transparent"
            >
              ‹
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
              const isCurrent = p === page;
              return (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={
                    "rounded-xl px-2 py-1.5 text-sm font-semibold border " +
                    (isCurrent
                      ? "border-blue-600 bg-blue-600 text-white font-semibold"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50")
                  }
                >
                  {String(p).padStart(2, "0")}
                </button>
              );
            })}
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              className="w-9 h-9 rounded-xl border border-slate-200 hover:bg-slate-50 font-black text-slate-700 disabled:opacity-50 disabled:hover:bg-transparent"
            >
              ›
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
